import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Typography,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    IconButton,
    Switch,
    FormControlLabel,
    ToggleButton,
    ToggleButtonGroup,
    Divider
} from '@mui/material';
import {
    Add as AddIcon,
    Public as GlobalIcon,
    Home as CustomIcon,
} from '@mui/icons-material';
import Table from '../../components/Table/Table';
import ConfirmationDialog from "../../utils/confirmationDialog";
import ShareIcon from '@mui/icons-material/Share';
import FamilyRestroomIcon from '@mui/icons-material/FamilyRestroom';
import HelpIcon from '@mui/icons-material/HelpOutline';
import Tooltip from '@mui/material/Tooltip';
import TenancySelectionComponent from "../../components/TenancySelectionComponent";
import {
    useDeletePresetRequestMutation,
    useCreatePresetRequestMutation,
    useUpdatePresetRequestMutation
} from '../../store/services/delivery-service';
import { useGetSimulationsQuery, useGetSimulationCollectionsQuery } from "../../store/services/simulation-service";
import { useGetCoursesQuery, useGetContentPacksQuery } from '../../store/services/university-service';
import { getCardColor } from "../../utils/colorUtils";
import MainCard from "../../components/cards/MainCard";
import { useTheme } from "@mui/material/styles";
import SharingDisplay from '../../components/ContentSharing/SharingDisplay';
import { MODAL_BORDER_RADIUS } from './utils/dateUtils';
import PresetPreviewDialog from './components/modals/PresetPreviewDialog';

// Helper function to calculate date from month-day pattern
const calculateDateFromMonthDay = (startDate, relativeMonth, dayOfMonth) => {
    if (!startDate) return null;

    try {
        const result = new Date(startDate);

        // Add months (relativeMonth is 1-based, so subtract 1)
        result.setMonth(result.getMonth() + (relativeMonth - 1));

        // Check if the day exists in this month
        const daysInMonth = new Date(result.getFullYear(), result.getMonth() + 1, 0).getDate();

        // Adjust for month end if needed
        const targetDay = dayOfMonth > daysInMonth ? daysInMonth : dayOfMonth;

        result.setDate(targetDay);

        return result;
    } catch (error) {
        console.error("Error calculating date:", error);
        return null;
    }
};

// Helper function to get display name for content
const getContentDisplayName = (item) => {
    if (item.contentType === 'RandomSimulation') {
        return item.contentName || 'Random Simulation Collection';
    }
    return item.contentName || item.contentId;
};

// Helper function to get display content type
const getDisplayContentType = (contentType) => {
    switch (contentType) {
        case 'RandomSimulation':
            return 'Random Simulation';
        case 'Simulation':
            return 'Simulation';
        case 'Course':
            return 'Course';
        case 'ContentPack':
            return 'Content Pack';
        default:
            return contentType;
    }
};

// Helper function to get chip color for content type
const getContentTypeColor = (contentType) => {
    switch (contentType) {
        case 'Simulation':
        case 'RandomSimulation':
            return 'primary';
        case 'Course':
        case 'ContentPack':
            return 'warning';
        default:
            return 'default';
    }
};

const PresetDeliveryTable = ({ presetData, error, isLoading, showNotification, onViewCalendar, isAdmin }) => {

    const [paginationModel, setPaginationModel] = useState({
        pageSize: 20,
        page: 0
    });

    // Items table pagination in the preview modal
    const [itemsPaginationModel, setItemsPaginationModel] = useState({
        pageSize: 20,
        page: 0
    });

    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [dialogAction, setDialogAction] = useState(null);
    const [dialogTitle, setDialogTitle] = useState('');
    const [dialogContent, setDialogContent] = useState('');
    const [selectedPresetId, setSelectedPresetId] = useState(null);
    const [presetDetailOpen, setPresetDetailOpen] = useState(false);
    const [selectedPreset, setSelectedPreset] = useState(null);

    // State for create/edit preset dialog
    const [presetFormOpen, setPresetFormOpen] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [isGlobalPreset, setIsGlobalPreset] = useState(false);
    const [presetFormData, setPresetFormData] = useState({
        name: '',
        items: [],
        sharedTenancyIds: isAdmin ? [] : [tenancyId],
        publishToFamily: false
    });

    // Track removed items for backend updates
    const [removedItemIds, setRemovedItemIds] = useState([]);

    // For adding/editing items in the preset
    const [itemFormOpen, setItemFormOpen] = useState(false);
    const [isEditingItem, setIsEditingItem] = useState(false);
    const [currentItem, setCurrentItem] = useState({
        relativeMonth: 1,
        dayOfMonth: 1,
        contentType: 'Simulation',
        contentId: '',
        contentName: '',
        adjustForMonthEnd: true,
        adjustForWeekends: true,
        // Batch send fields
        isBatchSend: false,
        batchDurationDays: 7,
        sendingTimeStart: '09:00',
        sendingTimeEnd: '17:00',
        pauseWeekends: false
    });
    const [editingItemIndex, setEditingItemIndex] = useState(-1);

    const navigate = useNavigate();
    const theme = useTheme();
    const lightMode = theme.palette.mode === "light";

    // Get user and tenancy information from Redux store
    const { tenancyId } = useSelector((state) => state.tenancy);
    const { role } = useSelector((state) => state.auth.user || {});

    // Get simulations and courses for content selection
    const { data: simulationData } = useGetSimulationsQuery({
        perPage: 500,
        page: 0,
        includes: "",
        query: "",
        additionalQueryParams: `&published=true&tenancyId=${tenancyId}`
    });

    const { data: courseData } = useGetCoursesQuery({
        perPage: 500,
        page: 0,
        includes: "",
        query: "",
        additionalQueryParams: `&published=true&tenancyId=${tenancyId}`
    });

    // Get simulation collections for random simulations
    const { data: collectionData } = useGetSimulationCollectionsQuery({
        perPage: 500,
        page: 0,
        includes: "",
        query: "",
        additionalQueryParams: `&published=true&tenancyId=${tenancyId}`
    });

    // Get content packs
    const { data: contentPackData } = useGetContentPacksQuery({
        perPage: 500,
        page: 0,
        searchTerm: '',
        tenancyId: tenancyId
    });

    // API mutations
    const [deletePresetRequest, { isLoading: isDeleting }] = useDeletePresetRequestMutation();
    const [createPresetRequest, { isLoading: isCreating }] = useCreatePresetRequestMutation();
    const [updatePresetRequest, { isLoading: isUpdating }] = useUpdatePresetRequestMutation();

    // Reset form data
    const resetFormData = () => {
        setPresetFormData({
            name: '',
            items: []
        });
        setIsGlobalPreset(false);
        setRemovedItemIds([]); // Reset removed items tracking
    };

    // Handle opening the create preset dialog
    const handleCreatePresetDialog = () => {
        setIsEditMode(false);
        resetFormData();
        setPresetFormOpen(true);
    };

    // Handle opening the edit preset dialog
    const handleEditPresetDialog = (preset) => {
        setIsEditMode(true);
        setPresetFormData({
            id: preset.id,
            name: preset.name,
            items: [...preset.items],
            tenancyId: preset.tenancyId,
            sharedTenancyIds: preset.sharedTenancyIds || [],
            publishToFamily: preset.publishToFamily || false
        });
        setRemovedItemIds([]);
        setPresetFormOpen(true);
    };

    // Handle creating or updating a preset
    const handleSavePreset = async () => {
        try {
            if (isEditMode) {
                const presetData = {
                    presetId: presetFormData.id,
                    name: presetFormData.name,
                    newItems: presetFormData.items.filter(item => !item.id).map(item => ({
                        contentId: item.contentId,
                        contentName: item.contentName,
                        contentType: item.contentType,
                        relativeMonth: item.relativeMonth,
                        dayOfMonth: item.dayOfMonth,
                        adjustForMonthEnd: item.adjustForMonthEnd !== undefined ? item.adjustForMonthEnd : true,
                        adjustForWeekends: item.adjustForWeekends !== undefined ? item.adjustForWeekends : true,
                        // Batch send fields
                        isBatchSend: item.isBatchSend || false,
                        batchDurationDays: item.isBatchSend ? (item.batchDurationDays || 7) : null,
                        sendingTimeStart: item.isBatchSend ? (item.sendingTimeStart || '') : '',
                        sendingTimeEnd: item.isBatchSend ? (item.sendingTimeEnd || '') : '',
                        pauseWeekends: item.isBatchSend ? (item.pauseWeekends || false) : false
                    })),
                    itemUpdates: presetFormData.items.filter(item => item.id).map(item => ({
                        itemId: item.id,
                        contentId: item.contentId,
                        contentType: item.contentType,
                        relativeMonth: item.relativeMonth,
                        dayOfMonth: item.dayOfMonth,
                        adjustForMonthEnd: item.adjustForMonthEnd,
                        adjustForWeekends: item.adjustForWeekends,
                        // Batch send fields
                        isBatchSend: item.isBatchSend || false,
                        batchDurationDays: item.isBatchSend ? (item.batchDurationDays || 7) : null,
                        sendingTimeStart: item.isBatchSend ? (item.sendingTimeStart || '') : '',
                        sendingTimeEnd: item.isBatchSend ? (item.sendingTimeEnd || '') : '',
                        pauseWeekends: item.isBatchSend ? (item.pauseWeekends || false) : false
                    })),
                    itemIdsToRemove: removedItemIds,
                    sharedTenancyIds: isAdmin ? presetFormData.sharedTenancyIds : [tenancyId],
                    publishToFamily: presetFormData.publishToFamily
                };


                await updatePresetRequest(presetData).unwrap();
                showNotification('Template updated successfully', 'success');
            } else {
                await createPresetRequest({
                    name: presetFormData.name,
                    presetItems: presetFormData.items.map(item => ({
                        contentId: item.contentId,
                        contentType: item.contentType,
                        contentName: item.contentName,
                        relativeMonth: item.relativeMonth,
                        dayOfMonth: item.dayOfMonth,
                        adjustForMonthEnd: item.adjustForMonthEnd !== undefined ? item.adjustForMonthEnd : true,
                        adjustForWeekends: item.adjustForWeekends !== undefined ? item.adjustForWeekends : false,
                        // Batch send fields
                        isBatchSend: item.isBatchSend || false,
                        batchDurationDays: item.isBatchSend ? (item.batchDurationDays || 7) : null,
                        sendingTimeStart: item.isBatchSend ? (item.sendingTimeStart || '') : '',
                        sendingTimeEnd: item.isBatchSend ? (item.sendingTimeEnd || '') : '',
                        pauseWeekends: item.isBatchSend ? (item.pauseWeekends || false) : false
                    })),
                    sharedTenancyIds: isAdmin ? presetFormData.sharedTenancyIds : [tenancyId],
                    publishToFamily: presetFormData.publishToFamily
                }).unwrap();
                showNotification('Template created successfully', 'success');
            }
            setPresetFormOpen(false);
            setRemovedItemIds([]);
        } catch (err) {
            console.error("Error saving preset:", err);
            showNotification('Failed to save template: ' + (err.data?.message || err.message || 'Unknown error'), 'error');
        }
    };

    // Handle opening the item form dialog for adding/editing an item
    const handleItemFormDialog = (item = null, index = -1) => {
        if (item) {
            // When editing, make a deep copy of the item to avoid reference issues
            setCurrentItem({ ...item });


            setEditingItemIndex(index);
            setIsEditingItem(true);
        } else {
            // Reset for new item
            setCurrentItem({
                relativeMonth: 1,
                dayOfMonth: 1,
                contentType: 'Simulation',
                contentId: '',
                contentName: '',
                adjustForMonthEnd: true,
                adjustForWeekends: true,
                // Batch send fields
                isBatchSend: false,
                batchDurationDays: 7,
                sendingTimeStart: '09:00',
                sendingTimeEnd: '17:00',
                pauseWeekends: false
            });
            setEditingItemIndex(-1);
            setIsEditingItem(false);
        }
        setItemFormOpen(true);
    };

    // Handle content type change
    const handleContentTypeChange = (e) => {
        setCurrentItem(prev => ({
            ...prev,
            contentType: e.target.value,
            contentId: '',
            contentName: ''
        }));
    };

    // Handle content selection
    const handleContentSelection = (e) => {
        const selectedValue = e.target.value;

        if (currentItem.contentType === 'RandomSimulation') {
            // For Random Simulation, find the collection
            const selectedCollection = collectionData?.find(col => col.internalId === selectedValue);
            setCurrentItem(prev => ({
                ...prev,
                contentId: selectedValue, // Store internalId
                contentName: selectedCollection?.collectionName || selectedValue // Store collection name for display
            }));
        } else if (currentItem.contentType === 'ContentPack') {
            // For Content Pack, find the pack
            const selectedPack = contentPackData?.find(pack => pack.internalId === selectedValue);
            setCurrentItem(prev => ({
                ...prev,
                contentId: selectedValue,
                contentName: selectedPack?.contentPackName || selectedValue
            }));
        } else {
            // For regular simulations and courses
            setCurrentItem(prev => ({
                ...prev,
                contentId: selectedValue,
                contentName: selectedValue
            }));
        }
    };

    // Handle saving an item to the preset
    const handleSaveItem = () => {
        if (!currentItem.contentId) {
            showNotification('Please select content', 'error');
            return;
        }

        // Additional validation for Random Simulation
        if (currentItem.contentType === 'RandomSimulation' && !currentItem.contentName) {
            showNotification('Please select a valid collection', 'error');
            return;
        }

        // Validate batch send times
        if (currentItem.isBatchSend && (!currentItem.sendingTimeStart || !currentItem.sendingTimeEnd)) {
            showNotification('Please provide both start and end times for batch sending', 'error');
            return;
        }

        // Create a copy of the items array
        const updatedItems = [...presetFormData.items];


        if (isEditingItem && editingItemIndex >= 0) {
            // Update existing item at the specified index

            if (editingItemIndex >= updatedItems.length) {
                console.error("Index out of bounds:", editingItemIndex, "Array length:", updatedItems.length);
                showNotification('Error updating item: index out of bounds', 'error');
                return;
            }

            updatedItems[editingItemIndex] = { ...currentItem };
        } else {
            // Add new item
            updatedItems.push({ ...currentItem });
        }

        // Update the form data with the modified items array
        setPresetFormData({
            ...presetFormData,
            items: updatedItems
        });

        // Reset form state
        setItemFormOpen(false);
        setIsEditingItem(false);
        setEditingItemIndex(-1);

        showNotification(
            isEditingItem ? 'Item updated successfully' : 'Item added successfully',
            'success'
        );
    };

    // Handle removing an item from the preset
    const handleRemoveItem = (index) => {
        const updatedItems = [...presetFormData.items];
        const removedItem = updatedItems[index];

        // If the item has an ID (existing item), add it to removedItemIds
        if (removedItem.id) {
            setRemovedItemIds(prev => [...prev, removedItem.id]);
        }

        // Remove the item from the items array
        updatedItems.splice(index, 1);

        setPresetFormData({
            ...presetFormData,
            items: updatedItems
        });

        showNotification('Item removed', 'info');
    };

    // Handle deleting a preset template
    const handleDeletePreset = (preset) => {
        setSelectedPresetId(preset.id);
        setDialogTitle('Delete Template');
        setDialogContent('Are you sure you want to delete this template? This action cannot be undone.');
        setDialogAction(() => async () => {
            try {
                await deletePresetRequest(preset.id).unwrap();
                showNotification('Template deleted successfully', 'success');
            } catch (err) {
                showNotification('Failed to delete template: ' + (err.data?.message || 'Unknown error'), 'error');
            }
        });
        setConfirmDialogOpen(true);
    };

    // Handle viewing preset details
    const handleViewPreset = (preset) => {
        setSelectedPreset(preset);
        setPresetDetailOpen(true);
    };

    // Format date as string
    const formatDate = (date) => {
        if (!date) return "";
        return date.toLocaleDateString('en-GB');
    };

    // Check if user can edit this preset
    const canEditPreset = (preset) => {
        // Super admins can edit any preset
        if (isAdmin) return true;

        // Regular tenancy admins can only edit their own custom presets
        return false
    };

    // Check if user can delete this preset
    const canDeletePreset = (preset) => {
        // Super admins can delete any preset
        if (isAdmin) return true;

        // Regular tenancy admins can only delete their own custom presets
        return false
    };

    const headCells = [
        {
            id: "preview",
            label: "",
            render: (dataRow) => (
                <Button
                    onClick={() => handleViewPreset(dataRow)}
                    size="small"
                    variant="contained"
                    color="primary"
                >
                    Preview
                </Button>
            ),
            initialVisible: true
        },
        {
            id: "edit",
            label: "",
            render: (dataRow) => {
                if (!canEditPreset(dataRow)) return null;

                return (
                    <Button
                        onClick={() => {
                            handleEditPresetDialog(dataRow);
                        }}
                        size="small"
                        variant="contained"
                        color="secondary"
                    >
                        Edit
                    </Button>
                );
            },
            initialVisible: true
        },
        {
            id: 'name',
            label: 'Name',
            initialVisible: true
        },
        {
            id: 'itemCount',
            label: 'Items',
            initialVisible: true,
            render: (row) => row.items?.length || 0
        },
        {
            id: "type",
            label: "Sharing",
            render: (row) => (
                <SharingDisplay
                    published={true}
                    sharedTenancyIds={row.sharedTenancyIds}
                    publishToFamily={row.publishToFamily}
                    variant="compact"
                />
            ),
            initialVisible: true
        },
        {
            id: 'createdAt',
            label: 'Created At',
            initialVisible: true,
            render: (row) => new Date(row.createdAt).toLocaleString('en-GB')
        },
        {
            id: 'updatedAt',
            label: 'Updated At',
            initialVisible: true,
            render: (row) => new Date(row.updatedAt).toLocaleString('en-GB')
        },
        {
            id: "delete",
            label: "",
            render: (dataRow) => {
                if (!canDeletePreset(dataRow)) return null;

                return (
                    <Button
                        onClick={() => handleDeletePreset(dataRow)}
                        size="small"
                        variant="contained"
                        color="error"
                        disabled={isDeleting && selectedPresetId === dataRow.id}
                    >
                        {isDeleting && selectedPresetId === dataRow.id ? (
                            <CircularProgress size={20} color="inherit" />
                        ) : (
                            "Delete"
                        )}
                    </Button>
                );
            },
            initialVisible: true
        },
    ];

    const editItemsHeadCells = [
        {
            id: 'edit',
            label: '',
            initialVisible: true,
            render: (row, _, tableIndex) => {
                const actualIndex = presetFormData.items.findIndex(item =>
                    item.contentId === row.contentId &&
                    item.contentType === row.contentType &&
                    item.relativeMonth === row.relativeMonth &&
                    item.dayOfMonth === row.dayOfMonth
                );

                return (
                    <Button
                        variant="contained"
                        color="secondary"
                        size="small"
                        onClick={() => handleItemFormDialog(row, actualIndex)}
                    >
                        Edit
                    </Button>
                );
            }
        },
        {
            id: 'relativeMonth',
            label: 'Month',
            initialVisible: true,
            render: (row) => (
                <Chip
                    label={`Month ${row.relativeMonth}`}
                    color="default"
                    variant="outlined"
                    size="small"
                />
            )
        },
        {
            id: 'dayOfMonth',
            label: 'Day',
            initialVisible: true,
            render: (row) => (
                <Chip
                    label={`Day ${row.dayOfMonth}`}
                    color="secondary"
                    variant="outlined"
                    size="small"
                />
            )
        },
        {
            id: 'contentType',
            label: 'Content Type',
            initialVisible: true,
            render: (row) => (
                <Chip
                    label={getDisplayContentType(row.contentType)}
                    color={getContentTypeColor(row.contentType)}
                    size="small"
                />
            )
        },
        {
            id: 'contentId',
            label: 'Content Name',
            initialVisible: true,
            render: (row) => getContentDisplayName(row)
        },
        {
            id: 'delivery',
            label: 'Delivery',
            initialVisible: true,
            render: (row) => {
                if (row.isBatchSend) {
                    return (
                        <Tooltip title={
                            <Box>
                                <Typography variant="caption" display="block">
                                    Duration: {row.batchDurationDays > 0 ? row.batchDurationDays : 'N/A'} days
                                </Typography>
                                <Typography variant="caption" display="block">
                                    Time: {row.sendingTimeStart || '00:00'} - {row.sendingTimeEnd || '23:59'} UTC
                                </Typography>
                                <Typography variant="caption" display="block">
                                    Pause weekends: {row.pauseWeekends ? 'Yes' : 'No'}
                                </Typography>
                            </Box>
                        }>
                            <Chip
                                label="Batch"
                                color="secondary"
                                variant="outlined"
                                size="small"
                            />
                        </Tooltip>
                    );
                }
                return (
                    <Chip
                        label="Direct"
                        color="default"
                        variant="outlined"
                        size="small"
                    />
                );
            }
        },
        {
            id: 'delete',
            label: '',
            initialVisible: true,
            render: (row, _, tableIndex) => {
                const actualIndex = presetFormData.items.findIndex(item =>
                    item.contentId === row.contentId &&
                    item.contentType === row.contentType &&
                    item.relativeMonth === row.relativeMonth &&
                    item.dayOfMonth === row.dayOfMonth
                );

                return (
                    <Button
                        variant="contained"
                        color="error"
                        size="small"
                        onClick={() => handleRemoveItem(actualIndex)}
                    >
                        Remove
                    </Button>
                );
            }
        }
    ];

    // Toolbar actions
    const toolbarActions = [
        {
            render: () => (
                <Box width={"100%"} justifyContent={"flex-end"} display={"flex"} gap={2}>
                    <Button
                        size="medium"
                        variant="contained"
                        sx={{ textTransform: "capitalize", whiteSpace: "nowrap", ml: 2 }}
                        onClick={() => handleCreatePresetDialog()}
                        endIcon={<AddIcon />}
                    >
                        Create Template
                    </Button>
                </Box>
            )
        }
    ];

    return (
        <>
            <Table
                name="Journey Templates"
                data={presetData?.presets || []}
                error={error}
                loading={isLoading}
                headCells={headCells}
                search={true}
                selected={[]}
                setSelected={() => { }}
                checkbox={false}
                toolbarActions={[
                    {
                        render: () => (
                            <Box width={"100%"} justifyContent={"flex-end"} display={"flex"} gap={2}>
                                <Button
                                    size="medium"
                                    variant="contained"
                                    sx={{ textTransform: "capitalize", whiteSpace: "nowrap", ml: 2 }}
                                    onClick={() => handleCreatePresetDialog()}
                                    endIcon={<AddIcon />}
                                >
                                    Create Template
                                </Button>
                            </Box>
                        )
                    }
                ]}
                showToolbarActions={true}
                maxTableHeight="calc(100vh - 280px)"
                paginationModel={paginationModel}
                setPaginationModel={setPaginationModel}
                rowCount={presetData?.totalRecords || 0}
                initialVisibleColumns={headCells.filter(cell => cell.initialVisible).map(cell => cell.label)}
            />

            {/* Preview Preset Dialog */}
            <PresetPreviewDialog
                open={presetDetailOpen}
                onClose={() => setPresetDetailOpen(false)}
                preset={selectedPreset}
                onEdit={canEditPreset(selectedPreset) ? (preset) => {
                    setPresetDetailOpen(false);
                    handleEditPresetDialog(preset);
                } : undefined}
            />

            {/* Create/Edit Preset Dialog */}
            <Dialog
                open={presetFormOpen}
                onClose={() => setPresetFormOpen(false)}
                maxWidth="lg"
                fullWidth
                PaperProps={{
                    sx: {
                        width: '80%',
                        height: '90vh',
                        maxWidth: '1200px',
                        maxHeight: '1000px',
                        bgcolor: 'background.default',
                        borderRadius: MODAL_BORDER_RADIUS
                    }
                }}
            >
                <DialogTitle>
                    {isEditMode ? 'Edit Template' : 'Create New Template'}
                    {' '}
                    {(isEditMode || (presetFormData.sharedTenancyIds && presetFormData.sharedTenancyIds.length > 0)) && (
                        <Chip
                            icon={presetFormData.sharedTenancyIds && presetFormData.sharedTenancyIds.includes(0) ? <GlobalIcon /> : <CustomIcon />}
                            label={presetFormData.sharedTenancyIds && presetFormData.sharedTenancyIds.includes(0) ? "Global Template" : "Custom Template"}
                            color={presetFormData.sharedTenancyIds && presetFormData.sharedTenancyIds.includes(0) ? "primary" : "default"}
                            size="small"
                            sx={{ ml: 1 }}
                        />
                    )}
                </DialogTitle>
                <DialogContent sx={{ overflowY: 'auto' }}>
                    <Box sx={{ mt: 2, mb: 3 }}>
                        <TextField
                            label="Template Name"
                            value={presetFormData.name}
                            onChange={(e) => setPresetFormData({ ...presetFormData, name: e.target.value })}
                            fullWidth
                            required
                            error={!presetFormData.name}
                            helperText={!presetFormData.name ? "Template name is required" : ""}
                        />
                    </Box>

                    {/* Template Sharing Card */}
                    <MainCard content={true} boxShadow={true} border={false}
                        sx={{ bgcolor: getCardColor(lightMode), mb: 2 }}>
                        <Typography variant="h4" gutterBottom>Template Sharing Settings</Typography>

                        {/* Tenancy Selection Component - only editable for admins */}
                        {isAdmin ? (
                            <Box sx={{ mb: 2 }}>
                                <TenancySelectionComponent
                                    selectedTenancies={presetFormData.sharedTenancyIds || []}
                                    onChange={(tenancyIds) => {
                                        // Check if global tenancy (0) is being added
                                        const isNowGlobal = tenancyIds.includes(0);
                                        // Update the state
                                        setPresetFormData({
                                            ...presetFormData,
                                            sharedTenancyIds: tenancyIds
                                        });
                                        // Update isGlobalPreset state based on whether tenancy 0 is included
                                        setIsGlobalPreset(isNowGlobal);
                                    }}
                                />
                            </Box>
                        ) : (
                            <Box sx={{ mb: 2 }}>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    This template will be shared with your tenancy only.
                                </Typography>
                                <TextField
                                    fullWidth
                                    label="Shared With"
                                    value="Your Tenancy"
                                    disabled
                                    size="small"
                                />
                            </Box>
                        )}

                        {/* Publish to Family Toggle */}
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 2 }}>
                            <Box sx={{ display: "flex", alignItems: "center" }}>
                                <Typography variant="body1">Share with Tenancy Family</Typography>
                                <Tooltip title="When enabled, this template will be accessible to all descendants of the selected tenancies" placement="top">
                                    <IconButton size="small">
                                        <HelpIcon sx={{ color: "secondary.main" }} />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                            <Switch
                                checked={presetFormData.publishToFamily || false}
                                onChange={(e) => setPresetFormData({
                                    ...presetFormData,
                                    publishToFamily: e.target.checked
                                })}
                                inputProps={{ "aria-label": "publish to family" }}
                                disabled={presetFormData.sharedTenancyIds?.includes(0)}
                            />
                        </Box>
                    </MainCard>

                    <Box sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mb: 2
                    }}>
                        <Typography variant="h6">
                            Content Items ({presetFormData.items?.length || 0})
                        </Typography>

                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={<AddIcon />}
                            onClick={() => handleItemFormDialog()}
                        >
                            Add Item
                        </Button>
                    </Box>

                    {/* Items table */}
                    <Table
                        name=""
                        data={presetFormData.items || []}
                        error={null}
                        loading={false}
                        headCells={editItemsHeadCells}
                        search={false}
                        selected={[]}
                        setSelected={() => { }}
                        checkbox={false}
                        toolbarActions={[]}
                        showToolbarActions={false}
                        maxTableHeight="535px"
                        paginationModel={itemsPaginationModel}
                        setPaginationModel={setItemsPaginationModel}
                        rowCount={presetFormData.items?.length || 0}
                        disablePagination={false}
                        size="small"
                        initialVisibleColumns={editItemsHeadCells.filter(cell => cell.initialVisible).map(cell => cell.label)}
                        emptyMessage="No items added. Click 'Add Item' to create your first item."
                    />
                </DialogContent>
                <DialogActions sx={{ p: 2, gap: 1 }}>
                    <Button
                        variant="contained"
                        color="warning"
                        onClick={() => setPresetFormOpen(false)}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSavePreset}
                        variant="contained"
                        color="primary"
                        disabled={!presetFormData.name || presetFormData.items.length === 0 || isCreating || isUpdating}
                    >
                        {isCreating || isUpdating ? <CircularProgress size={24} /> : 'Save Template'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Add/Edit Item Dialog */}
            <Dialog
                open={itemFormOpen}
                onClose={() => setItemFormOpen(false)}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: { bgcolor: 'background.default', borderRadius: MODAL_BORDER_RADIUS }
                }}
            >
                <DialogTitle>
                    {isEditingItem ? 'Edit Content Item' : 'Add Content Item'}
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ mt: 2, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 3 }}>
                        {/* Left column - Scheduling */}
                        <Box>
                            <Typography variant="h6" gutterBottom>Scheduling</Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                                <Box>
                                    <TextField
                                        label="Month"
                                        type="number"
                                        value={currentItem.relativeMonth === '' ? '' : currentItem.relativeMonth}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            setCurrentItem({
                                                ...currentItem,
                                                relativeMonth: value === '' ? '' : parseInt(value, 10)
                                            });
                                        }}
                                        onBlur={(e) => {
                                            const value = e.target.value;
                                            if (value === '' || isNaN(parseInt(value, 10)) || parseInt(value, 10) < 1) {
                                                setCurrentItem({
                                                    ...currentItem,
                                                    relativeMonth: 1
                                                });
                                            }
                                        }}
                                        fullWidth
                                        inputProps={{ min: 1 }}
                                        sx={{ mb: 1 }}
                                    />
                                    <Typography variant="caption" color="text.secondary">
                                        Month 1 is the first month. Use 2, 3, etc. for subsequent months.
                                    </Typography>
                                </Box>

                                <Box>
                                    <TextField
                                        label="Day of Month"
                                        type="number"
                                        value={currentItem.dayOfMonth === '' ? '' : currentItem.dayOfMonth}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            setCurrentItem({
                                                ...currentItem,
                                                dayOfMonth: value === '' ? '' : Math.min(31, parseInt(value, 10))
                                            });
                                        }}
                                        onBlur={(e) => {
                                            const value = e.target.value;
                                            if (value === '' || isNaN(parseInt(value, 10)) || parseInt(value, 10) < 1) {
                                                setCurrentItem({
                                                    ...currentItem,
                                                    dayOfMonth: 1
                                                });
                                            }
                                        }}
                                        fullWidth
                                        inputProps={{ min: 1, max: 31 }}
                                        sx={{ mb: 1 }}
                                    />
                                    <Typography variant="caption" color="text.secondary">
                                        Day of the month (1-31)
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>

                        {/* Right column - Content Selection */}
                        <Box>
                            <Typography variant="h6" gutterBottom>Content</Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                                <Box>
                                    <FormControl fullWidth sx={{ mb: 1 }}>
                                        <InputLabel>Content Type</InputLabel>
                                        <Select
                                            value={currentItem.contentType}
                                            label="Content Type"
                                            onChange={handleContentTypeChange}
                                        >
                                            <MenuItem value="Simulation">Simulation</MenuItem>
                                            <MenuItem value="RandomSimulation">Random Simulation</MenuItem>
                                            <MenuItem value="Course">Course</MenuItem>
                                            <MenuItem value="ContentPack">Content Pack</MenuItem>
                                        </Select>
                                    </FormControl>
                                    <Typography variant="caption" color="text.secondary">
                                        Select the type of content to add
                                    </Typography>
                                </Box>

                                <Box>
                                    <FormControl fullWidth sx={{ mb: 1 }}>
                                        <InputLabel>
                                            Select {currentItem.contentType === 'RandomSimulation' ? 'Collection' : currentItem.contentType === 'ContentPack' ? 'Content Pack' : currentItem.contentType}
                                        </InputLabel>
                                        <Select
                                            value={currentItem.contentId}
                                            label={`Select ${currentItem.contentType === 'RandomSimulation' ? 'Collection' : currentItem.contentType === 'ContentPack' ? 'Content Pack' : currentItem.contentType}`}
                                            onChange={handleContentSelection}
                                        >
                                            {currentItem.contentType === 'Simulation' && simulationData && simulationData.map((sim, index) => (
                                                <MenuItem key={index} value={sim.simulationName}>
                                                    {sim.simulationName}
                                                </MenuItem>
                                            ))}
                                            {currentItem.contentType === 'RandomSimulation' && collectionData && collectionData.map((collection, index) => (
                                                <MenuItem key={index} value={collection.internalId}>
                                                    {collection.collectionName}
                                                </MenuItem>
                                            ))}
                                            {currentItem.contentType === 'Course' && courseData && courseData.map((course, index) => (
                                                <MenuItem key={index} value={course.courseName}>
                                                    {course.courseName}
                                                </MenuItem>
                                            ))}
                                            {currentItem.contentType === 'ContentPack' && contentPackData && contentPackData.map((pack, index) => (
                                                <MenuItem key={index} value={pack.internalId}>
                                                    {pack.contentPackName}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                    <Typography variant="caption" color="text.secondary">
                                        {currentItem.contentType === 'RandomSimulation'
                                            ? 'Choose a collection for random simulation selection'
                                            : currentItem.contentType === 'ContentPack'
                                                ? 'Choose a content pack to deliver'
                                                : 'Choose a specific content item'
                                        }
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>
                    </Box>

                    {/* Delivery Mode Section */}
                    <Divider sx={{ my: 3 }} />
                    <Box>
                        <Typography variant="h6" gutterBottom>Delivery Mode</Typography>
                        <Box sx={{ mb: 2 }}>
                            <ToggleButtonGroup
                                value={currentItem.isBatchSend ? 'batch' : 'direct'}
                                exclusive
                                onChange={(e, value) => {
                                    if (value !== null) {
                                        setCurrentItem({
                                            ...currentItem,
                                            isBatchSend: value === 'batch'
                                        });
                                    }
                                }}
                                fullWidth
                            >
                                <ToggleButton value="direct">
                                    Direct Send
                                </ToggleButton>
                                <ToggleButton value="batch">
                                    Batch Send
                                </ToggleButton>
                            </ToggleButtonGroup>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                                {currentItem.isBatchSend
                                    ? 'Content will be sent to recipients gradually over the specified duration'
                                    : 'Content will be sent to all recipients at once on the scheduled date'
                                }
                            </Typography>
                        </Box>

                        {/* Batch Send Options - only shown when batch send is enabled */}
                        {currentItem.isBatchSend && (
                            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 3, mt: 2 }}>
                                <Box>
                                    <TextField
                                        label="Batch Duration (Days)"
                                        type="number"
                                        value={currentItem.batchDurationDays === '' ? '' : currentItem.batchDurationDays}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            setCurrentItem({
                                                ...currentItem,
                                                batchDurationDays: value === '' ? '' : Math.min(31, parseInt(value, 10))
                                            });
                                        }}
                                        onBlur={(e) => {
                                            const value = e.target.value;
                                            if (value === '' || isNaN(parseInt(value, 10)) || parseInt(value, 10) < 1) {
                                                setCurrentItem({
                                                    ...currentItem,
                                                    batchDurationDays: 1
                                                });
                                            }
                                        }}
                                        fullWidth
                                        inputProps={{ min: 1, max: 31 }}
                                        sx={{ mb: 1 }}
                                    />
                                    <Typography variant="caption" color="text.secondary">
                                        Number of days to spread sending across (1-31)
                                    </Typography>
                                </Box>

                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={currentItem.pauseWeekends}
                                                onChange={(e) => setCurrentItem({
                                                    ...currentItem,
                                                    pauseWeekends: e.target.checked
                                                })}
                                            />
                                        }
                                        label="Pause on Weekends"
                                    />
                                </Box>

                                <Box>
                                    <TextField
                                        label="Sending Start Time"
                                        type="time"
                                        value={currentItem.sendingTimeStart}
                                        onChange={(e) => setCurrentItem({
                                            ...currentItem,
                                            sendingTimeStart: e.target.value
                                        })}
                                        fullWidth
                                        required
                                        error={!currentItem.sendingTimeStart}
                                        InputLabelProps={{ shrink: true }}
                                        sx={{ mb: 1 }}
                                    />
                                    <Typography variant="caption" color="text.secondary">
                                        Daily start time for sending (HH:mm, UTC)
                                    </Typography>
                                </Box>

                                <Box>
                                    <TextField
                                        label="Sending End Time"
                                        type="time"
                                        value={currentItem.sendingTimeEnd}
                                        onChange={(e) => setCurrentItem({
                                            ...currentItem,
                                            sendingTimeEnd: e.target.value
                                        })}
                                        fullWidth
                                        required
                                        error={!currentItem.sendingTimeEnd}
                                        InputLabelProps={{ shrink: true }}
                                        sx={{ mb: 1 }}
                                    />
                                    <Typography variant="caption" color="text.secondary">
                                        Daily end time for sending (HH:mm, UTC)
                                    </Typography>
                                </Box>
                            </Box>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 2, gap: 1 }}>
                    <Button
                        variant="contained"
                        color="warning"
                        onClick={() => setItemFormOpen(false)}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSaveItem}
                        variant="contained"
                        color="primary"
                        disabled={
                            !currentItem.contentId ||
                            (currentItem.isBatchSend && (!currentItem.sendingTimeStart || !currentItem.sendingTimeEnd))
                        }
                    >
                        {isEditingItem ? 'Update Item' : 'Add Item'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Confirmation Dialog */}
            <ConfirmationDialog
                open={confirmDialogOpen}
                title={dialogTitle}
                content={dialogContent}
                onConfirm={() => {
                    setConfirmDialogOpen(false);
                    dialogAction?.();
                }}
                onCancel={() => setConfirmDialogOpen(false)}
            />
        </>
    );
};

PresetDeliveryTable.propTypes = {
    showNotification: PropTypes.func.isRequired
};

export default PresetDeliveryTable;